import { useRef, useEffect, useCallback, useState } from 'react'
import { motion } from 'framer-motion'
import FileDropZone from '../FileDropZone'
import CascadeSpinner from './entries/CascadeSpinner'
import { useAgentTerminal } from './use-agent-terminal'
import type { TerminalEntry } from './types'
import type { JobWizardConfig, MachineProfile, Tool } from '../../../types'

// Entry renderers
import AgentTextLine from './entries/AgentTextLine'
import AgentStatusLine from './entries/AgentStatusLine'
import AgentQuestionBlock from './entries/AgentQuestionBlock'
import AgentSummaryBlock from './entries/AgentSummaryBlock'
import AgentErrorLine from './entries/AgentErrorLine'
import AgentPartValidation from './entries/AgentPartValidation'
import AgentToolsBlock from './entries/AgentToolsBlock'
import UserFileLine from './entries/UserFileLine'
import UserAnswerLine from './entries/UserAnswerLine'

interface AgentTerminalProps {
  profiles: MachineProfile[]
  inventoryTools?: Tool[]
  onSubmit: (config: JobWizardConfig) => void
  onGeometryChange?: (geo: Record<string, unknown> | null) => void
  onStepFileChange?: (file: File | null) => void
  onManageProfiles?: () => void
  onFilesSubmitted?: () => void
  /** Work offsets set from the 3D preview picker — synced to question fields */
  workOffsets?: Array<{ register: string; x: number; y: number; z: number }>
  /** STEP mesh bounds from 3D tessellation — more accurate than feature extraction */
  meshBounds?: { min: [number, number, number]; max: [number, number, number] } | null
}

export default function AgentTerminal({ profiles, inventoryTools = [], onSubmit, onGeometryChange, onStepFileChange, onManageProfiles, onFilesSubmitted, workOffsets: externalOffsets, meshBounds }: AgentTerminalProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const {
    entries, phase, config, isSuggestingTools, shapeSuggestions, partGeometry,
    submitFiles, submitAnswer, submitAdditionalFiles,
    confirmPart, rejectPart,
    confirmTools, updateToolsEntry, resuggestTools,
    editSummaryField,
    editAnswer,
    editMachine,
    editTools,
  } = useAgentTerminal(profiles, inventoryTools, onGeometryChange)

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [entries])

  const handleFilesChanged = useCallback((files: File[]) => {
    setStagedFiles(files)
    const step = files.find(f => /\.(step|stp)$/i.test(f.name)) ?? null
    onStepFileChange?.(step)
  }, [onStepFileChange])

  const handleBeginAnalysis = useCallback(() => {
    if (stagedFiles.length === 0) return
    const machineProfiles = profiles.map(p => ({
      id: p.id,
      name: p.name,
      type: p.type,
    }))
    submitFiles(stagedFiles, machineProfiles)
    setStagedFiles([])
    onFilesSubmitted?.()
  }, [stagedFiles, profiles, submitFiles, onFilesSubmitted])

  const handleGenerate = useCallback(() => {
    // Merge 3D picker offsets with backend-derived offsets (picker takes priority)
    const mergedOffsets: import('../../../types').WorkOffset[] = externalOffsets && externalOffsets.length > 0
      ? externalOffsets.map(o => ({ register: o.register as import('../../../types').WorkOffsetRegister, x: o.x, y: o.y, z: o.z }))
      : config.workOffsets || [{ register: 'G54' as const, x: 0, y: 0, z: 0 }]

    // If STEP mesh bounds available, refine the part_envelope in extractedGeometry
    let geometry = config.extractedGeometry || null
    if (geometry && meshBounds && geometry.part_envelope) {
      const env = geometry.part_envelope
      const sd = geometry.stock_dimensions as { z?: number } | undefined
      geometry = {
        ...geometry,
        part_envelope: {
          ...env,
          min_x: meshBounds.min[0],
          min_y: -meshBounds.max[2],
          max_x: meshBounds.max[0],
          max_y: -meshBounds.min[2],
          min_z: meshBounds.min[1] - (sd?.z || 0),
          max_z: meshBounds.max[1] - (sd?.z || 0),
        },
      }
    }

    const fullConfig: JobWizardConfig = {
      machineId: (config.machineId as string) || profiles[0]?.id || '',
      operationType: config.operationType || 'milling',
      material: config.material || 'AL 6061',
      stockShape: config.stockShape || 'Rectangular',
      stockSize: config.stockSize || null,
      workOffsets: mergedOffsets,
      drawing: null,
      tools: config.tools || [],
      notes: config.notes || '',
      knowledgeBase: config.knowledgeBase || [],
      extractedGeometry: geometry,
      pdfBase64: config.pdfBase64 || null,
    }
    onSubmit(fullConfig)
  }, [config, profiles, onSubmit, externalOffsets, meshBounds])

  const renderEntry = (entry: TerminalEntry) => {
    switch (entry.type) {
      case 'agent_text':
        return <AgentTextLine key={entry.id} text={entry.text} />
      case 'agent_status':
        return <AgentStatusLine key={entry.id} label={entry.label} status={entry.status} detail={entry.detail} />
      case 'agent_preview':
        // Preview is shown in the right panel via onGeometryChange — skip inline render
        return null
      case 'agent_question': {
        // Machine picker: derive options from the LIVE profiles list (filtered
        // to milling/both, matching the server) instead of the frozen server
        // snapshot, so a profile created mid-question is immediately selectable.
        // Empty → undefined so the create-a-profile CTA shows.
        const liveMachineOptions = profiles
          .filter(p => p.type === 'milling' || p.type === 'both')
          .map(p => ({ value: p.id, label: p.name }))
        return (
          <AgentQuestionBlock
            key={entry.id}
            field={entry.field}
            prompt={entry.prompt}
            inputType={entry.inputType}
            priority={entry.priority}
            options={
              entry.inputType === 'machine_picker'
                ? (liveMachineOptions.length > 0 ? liveMachineOptions : undefined)
                : entry.options
            }
            answered={entry.answered}
            onAnswer={submitAnswer}
            onFilesDropped={submitAdditionalFiles}
            onCreateNew={entry.inputType === 'machine_picker' ? onManageProfiles : undefined}
            externalOffsets={entry.field === 'workOrigin' ? externalOffsets : undefined}
            config={config}
            shapeSuggestions={shapeSuggestions}
            partGeometry={partGeometry ?? undefined}
          />
        )
      }
      case 'agent_summary':
        // Rendered separately at the bottom — skip in the normal flow
        return null
      case 'agent_part_validation':
        return (
          <AgentPartValidation
            key={entry.id}
            confirmed={entry.confirmed}
            onConfirm={confirmPart}
            onReject={rejectPart}
            stepValidation={entry.stepValidation}
          />
        )
      case 'agent_tools':
        return (
          <AgentToolsBlock
            key={entry.id}
            tools={entry.tools}
            confirmed={entry.confirmed}
            onToolsChange={updateToolsEntry}
            onConfirm={confirmTools}
            onResuggest={resuggestTools}
          />
        )
      case 'agent_error':
        return <AgentErrorLine key={entry.id} message={entry.message} />
      case 'user_files':
        return <UserFileLine key={entry.id} fileNames={entry.fileNames} />
      case 'user_answer':
        return <UserAnswerLine key={entry.id} field={entry.field} displayText={entry.displayText} onEdit={editAnswer} />
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable terminal log */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-0.5 pb-4"
        style={{ minHeight: 0 }}
      >
        {entries.length === 0 && phase === 'idle' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-8"
          >
            <p className="font-mono text-sm text-slate-500 mb-1">
              Drop your engineering files to begin.
            </p>
            <p className="font-mono text-xs text-slate-600">
              A DWG or DXF drawing is required. PDF optional — add one if it carries extra dimensions or notes.
            </p>
          </motion.div>
        )}
        {entries.map(renderEntry)}

        {/* Inline processing indicator — appears at the end of the entries */}
        {phase === 'processing' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 py-2 pl-1"
          >
            <CascadeSpinner className="text-cyan-400" />
            <span className="font-mono text-sm text-slate-400">
              Agent is working...
            </span>
          </motion.div>
        )}

        {/* Summary block — always rendered last, below everything else */}
        {entries.filter(e => e.type === 'agent_summary').map(entry =>
          entry.type === 'agent_summary' ? (
            <AgentSummaryBlock
              key={entry.id}
              config={entry.config}
              readyToGenerate={entry.readyToGenerate && phase === 'ready'}
              onGenerate={handleGenerate}
              onEditField={editSummaryField}
              onEditMachine={editMachine}
              onEditTools={editTools}
              profiles={profiles}
              isSuggestingTools={isSuggestingTools}
            />
          ) : null,
        )}
      </div>

      {/* Bottom input area */}
      <div className="shrink-0 pt-3 border-t border-cyan-500/10">
        {phase === 'idle' && (
          <div className="space-y-2">
            <FileDropZone
              files={stagedFiles}
              onFilesDropped={handleFilesChanged}
            />
            {stagedFiles.length > 0 && (
              <button
                type="button"
                onClick={handleBeginAnalysis}
                className="w-full py-2 rounded-lg font-mono text-xs font-semibold tracking-wider bg-gradient-to-r from-cyan-500 to-blue-500 text-gray-950 hover:from-cyan-400 hover:to-blue-400 transition-all duration-150 shadow-[0_0_16px_rgba(6,182,212,0.15)]"
              >
                Begin Analysis
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
